import { useState } from "react";
import { ChannelEnabledToggle } from "../../atoms/ChannelEnabledToggle";
import { SecretInput } from "../../atoms/SecretInput";
import { TestSendPanel } from "../../molecules/TestSendPanel";
import type { ChannelSettingResponse, SmsConfig, SmsProvider } from "../../../types/subscriptionSettings";
import type { UseChannelSettings } from "../../../hooks/useChannelSettings";

interface Props {
  ctrl: UseChannelSettings;
  setting: ChannelSettingResponse;
}

export function SmsChannelTab({ ctrl, setting }: Props) {
  const initial = setting.config as SmsConfig;
  const [enabled, setEnabled] = useState(setting.is_enabled);
  const [provider, setProvider] = useState<SmsProvider>(initial.provider ?? "twilio");
  const [apiKey, setApiKey] = useState(initial.api_key ?? "");
  const [authToken, setAuthToken] = useState("");
  const [fromNumber, setFromNumber] = useState(initial.from_number ?? "");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const errors = ctrl.fieldErrors.sms;
  const tokenIsSet = initial.auth_token?.is_set ?? false;

  async function handleSave() {
    setSaveStatus("saving");
    const config: Record<string, unknown> = {
      provider,
      api_key: apiKey,
      from_number: fromNumber,
    };
    if (authToken) config.auth_token = authToken;
    const result = await ctrl.save("sms", { is_enabled: enabled, config });
    setSaveStatus(result ? "saved" : "error");
    if (result) setAuthToken("");
  }

  return (
    <div className="ac-channel-tab">
      <div className="ac-channel-tab__header">
        <h2 className="ac-channel-tab__title">SMS</h2>
        <ChannelEnabledToggle enabled={enabled} onChange={setEnabled} />
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="sms-provider">Provider</label>
        <select id="sms-provider" className="ac-select" value={provider} onChange={(e) => setProvider(e.target.value as SmsProvider)}>
          <option value="twilio">Twilio</option>
          <option value="aws_sns">AWS SNS</option>
        </select>
        {errors.provider && <span className="ac-form-row__error">{errors.provider.join(" ")}</span>}
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="sms-key">API key / Account SID</label>
        <input id="sms-key" className="ac-input" value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
        {errors.api_key && <span className="ac-form-row__error">{errors.api_key.join(" ")}</span>}
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="sms-token">Auth token</label>
        <SecretInput id="sms-token" value={authToken} onChange={setAuthToken} isSet={tokenIsSet} ariaLabel="SMS auth token" />
        {errors.auth_token && <span className="ac-form-row__error">{errors.auth_token.join(" ")}</span>}
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="sms-from">From number (E.164)</label>
        <input id="sms-from" className="ac-input" value={fromNumber} onChange={(e) => setFromNumber(e.target.value)} placeholder="+14155550123" />
        {errors.from_number && <span className="ac-form-row__error">{errors.from_number.join(" ")}</span>}
      </div>

      <div className="ac-button-row">
        <button type="button" className="ac-button" onClick={handleSave} disabled={saveStatus === "saving"}>
          {saveStatus === "saving" ? "Saving..." : "Save"}
        </button>
        {saveStatus === "saved" && <span className="ac-alert ac-alert--success" role="status">Saved.</span>}
        {saveStatus === "error" && <span className="ac-alert ac-alert--error" role="status">Failed to save.</span>}
      </div>

      <TestSendPanel
        destinationLabel="Send test SMS to"
        placeholder="+14155550123"
        disabled={!enabled}
        onSend={(dest) => ctrl.test("sms", dest)}
      />
    </div>
  );
}
